import React, { useState } from 'react';
import { Text, View, Pressable } from 'react-native';
import { useUnistyles } from 'react-native-unistyles';
import Input from '@/components/General/inputs/Input';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { saveFiles } from '@/data/library';
import { formatSize, saveFile } from '@/utils/FileManager';
import FontAwesome5 from "@react-native-vector-icons/fontawesome5";
import * as ImagePicker from 'expo-image-picker';
import * as VideoThumbnails from 'expo-video-thumbnails';
import * as Progress from 'react-native-progress';
import Toast from 'react-native-toast-message'
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { Image } from 'expo-image';
import { useLocalSearchParams, useRouter } from 'expo-router';
import ModalWrapper from '@/components/General/ModalWrapper';
import Unicon from '@/components/General/Unicon';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';

export default function UploadPhotoModal() {
    const { parentFolder, path } = useLocalSearchParams()

    const [ files, setFiles ] = useState([])
    const [ progressAmounts, setProgressAmounts ] = useState([0])
    const [ loading, setLoading ] = useState(false)
    const [ uploading, setUploading ] = useState(false)

    const {theme: {colors, fonts}} = useUnistyles();
    const queryClient = useQueryClient()
    const router = useRouter()

    const { data: loggedInUser } = Query.people.loggedInUser()

    const saveFileMutation = useMutation({
        mutationFn: saveFiles,
        onMutate: () => {
            setUploading(true)
            setProgressAmounts([0.1])
        },
        onSuccess: async () => {

            const allUploads = [];

            files.forEach(async (file) => {
                allUploads.push( await saveFile(file.uri, `${loggedInUser?.organizationID}/${path}/${file.name.trim() + file.extension}`))
            })

            Promise.all(allUploads).then(() => {
                Toast.show({
                    type: 'success',
                    text2: `Upload(s) have been completed!`
                });

                queryClient.invalidateQueries({queryKey: QueryKeys.folders.byId(parentFolder?.id)})

                router.replace({
                    pathname: '/library/folders',
                    params: {
                        folderID: parentFolder?.id
                    },
                });
            })
            
        },
        onError: () => {
            setProgressAmounts([0])
            // console.debug(err)
        },
    })


    const choosePhoto = async () => {
        setTimeout(() => {
            setLoading(true);
        }, 1000)

        let result = await ImagePicker.launchImageLibraryAsync({
            mediaTypes: ImagePicker.MediaTypeOptions.All,
            allowsMultipleSelection: true,
            quality: 0.2,
            videoQuality: ImagePicker.UIImagePickerControllerQualityType.Medium
        });
    
        if (!result.canceled) {

            const chosenFiles = []
            result.assets.forEach(async (asset, index) => {
                const maxSizeDivisor = Math.max(asset.width, asset.height) / 150;

                if(asset.type == 'video'){
                    VideoThumbnails.getThumbnailAsync(asset.uri, { time: 0 })
                        .then(({uri}) => {
                            setFiles(prev => {
                                const prevFiles = [...prev];
                                prevFiles[index].thumbnail = uri;
                                return prevFiles;
                            });
                        })
                        .catch(() => {
                            // console.warn("Could not get video thumbnail", e);
                        })
                }

                const ext = "." + asset.fileName.split('.').pop();

                chosenFiles.push({
                    uri: asset.uri,
                    name: asset.fileName.replace(ext, ""),
                    extension: ext,
                    size: asset.fileSize,
                    formattedSize: formatSize(asset.fileSize),
                    type: asset.type == 'video' ? 'video/mp4' : 'image/jpg',
                    height: asset.height / maxSizeDivisor,
                    width: asset.width / maxSizeDivisor,
                    originalInfo: asset
                })
            })

            setFiles(chosenFiles);
            setLoading(false);
        }
        else{
            setLoading(false);
        }
    };

    const chosenFiles = files?.map((file, index) => {
        return (
            <View key={index} style={{paddingBottom:25}}>
                <View style={{width:"100%"}}>
                    <Input
                        title="File Name"
                        maxLength={120}
                        backgroundColor={colors.surface}
                        value={file.name}
                        onUpdate={(new_value) => {
                            const formattedValue = new_value.replace(/[^0-9a-zA-z _-]|[\^[\]\\]/g, '')

                            setFiles(old => {
                                const oldStuff = old
                                oldStuff[index] = {...oldStuff[index], name: formattedValue}
                                return [...oldStuff]
                            })
                        }}
                    />
                </View>
                <View style={{flexDirection:"row"}}>
                    <View style={{shadowColor: '#000', shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.3, shadowRadius: 8, height:file.height, width:file.width,}}>
                        { file.thumbnail && <View style={{zIndex:2, position:"absolute", top:0, right:0, width:32, borderTopRightRadius:5, borderBottomLeftRadius:5, justifyContent:"center", alignItems:"center", backgroundColor:colors.text, opacity:0.7}}><Unicon name="videocam" size={22} colorVariant="background" /></View> }
                        <Image source={{uri: file.thumbnail || file.uri}} style={{height:file.height, width:file.width, borderRadius:8 }} />
                    </View>
                    <View style={{paddingLeft:10}}>
                        <Text style={{paddingTop:0, fontSize:12, fontFamily:fonts.bold, color:colors.textSecondary}}>{file.formattedSize}</Text>
                        <Text style={{paddingTop:0, fontSize:12, fontFamily:fonts.body, color:colors.textSecondary}}>{file.originalInfo.height + "x" + file.originalInfo.width}</Text>
                    </View>
                </View>
            </View>
        );
    })


    const progressPercent = (progressAmounts.reduce((partialSum, a) => partialSum + a, 0) / progressAmounts.length)
    
    return (
        <ModalWrapper 
            loading={saveFileMutation.isPending} 
            noLoader 
            confirmText='Upload' 
            onConfirm={() => saveFileMutation.mutate({files: files, path: path, folderID: parentFolder.id, uploadedByID: loggedInUser?.id, orgID: loggedInUser?.organizationID})} 
            title="Upload from Photos"
        >
            <KeyboardAwareScrollView bottomOffset={100} keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
                <Pressable onPress={() => {
                    choosePhoto();
                }} style={{margin:15, borderWidth:1, borderColor:colors.textSecondary, borderStyle:"dashed", borderRadius:8, padding:10, paddingLeft:20, flexDirection:"row", gap:20}}>
                    <View style={{alignItems:"center", justifyContent:"center"}}><FontAwesome5 name="images" color={colors.textSecondary} size={32} iconStyle="solid" /></View>
                    <View style={{justifyContent:"center"}}>
                        <Text style={{fontSize:16, fontFamily:fonts.bold, color:colors.text}}>Select files to upload</Text>
                        <Text style={{paddingTop:5, fontSize:13, fontFamily:fonts.body, color:colors.textSecondary}}>Tap here to open the photo library on your device</Text>
                    </View>
                </Pressable>

                { loading && <View style={{alignItems:"center", justifyContent:"center"}}>
                    <Text style={{fontSize:20, fontFamily:fonts.bold, color:colors.text, paddingTop:25}}>Loading files...</Text>
                    <View style={{width:"100%", paddingTop:20}}><Progress.Bar progress={0} width={"180%"} borderWidth={0} borderRadius={3} height={3} color={colors.tertiary} indeterminateAnimationDuration={1000} indeterminate={true}/></View>
                </View> }

                { uploading && <View style={{alignItems:"center", justifyContent:"center"}}>
                    <Text style={{fontSize:20, fontFamily:fonts.bold, color:colors.text, paddingTop:25}}>Uploading files - {(progressPercent*100).toFixed(0)}%</Text>
                    <View style={{width:"100%", paddingTop:20}}><Progress.Bar progress={progressPercent} width={"100%"} borderWidth={1} borderRadius={3} height={5} color={colors.tertiary}/></View>
                </View> }

                { chosenFiles.length > 0 && <View>
                    <Text style={{fontSize:20, fontFamily:fonts.bold, color:colors.text, paddingTop:25}}>Chosen Files</Text>
                    <Text style={{paddingTop:5, fontSize:14, fontFamily:fonts.body, color:colors.textSecondary}}>These files will be uploaded with the given file names</Text>
                </View> }
                {chosenFiles}

            </KeyboardAwareScrollView>
        </ModalWrapper>
    );
}